import { FormProvider, useForm } from 'react-hook-form'

import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { FormSelect } from '@/components/ui/form/FormSelect'
import { Tag } from '@/components/ui/tag'
import type { ListUser } from '@/modules/chatting/types'
import { CreatorAvatar } from '@/modules/creators/ui/CreatorAvatar'

import { DialogProps } from '../types'
import {
  daysOptions,
  discountTypes,
  monthsOptions,
  promotionalOfferFormDefaultValues,
  subscriptionPercentOptions,
} from './consts'
import {
  PROMOTIONAL_OFFER_FOR_FAN_FORM_ID,
  PromotionalOfferForFanFormValues,
  promotionalOfferForFanFormResolver,
} from './validation'

interface Props extends DialogProps {
  user: ListUser
}

export const PromotionalOfferForFanDialog = ({
  isOpen,
  onClose,
  user,
}: Props) => {
  const form = useForm<PromotionalOfferForFanFormValues>({
    defaultValues: promotionalOfferFormDefaultValues,
    resolver: promotionalOfferForFanFormResolver,
  })

  const discountType = form.watch('type')

  const onSubmit = (values: PromotionalOfferForFanFormValues) => {
    return null
  }

  return (
    <Dialog
      bigDialog
      panelClassName="lg:max-w-[533px]"
      onClose={onClose}
      show={isOpen}
      showCloseIcon={false}
    >
      <div className="space-y-6">
        <Dialog.Header>
          <Dialog.Title>Promotional offer for</Dialog.Title>
        </Dialog.Header>
        <div className="flex gap-3">
          <CreatorAvatar
            name={user.displayName ?? user.name}
            avatarSrc={
              user.avatarThumbs?.c50 ?? user.avatarThumbs?.c144 ?? user.avatar
            }
            size={50}
            className="shrink-0"
          />
          <div className="flex flex-col justify-between">
            <p className="font-semibold leading-[26px]">
              {user.displayName ?? user.name}
            </p>
            <p className="text-secondary-300 text-sm leading-[22px]">
              {!!user.displayName && !!user.name && (
                <>
                  <span className="font-semibold">{user.name}</span>{' '}
                </>
              )}
              @{user.username}
            </p>
          </div>
        </div>
        <p className="text-xs leading-[18px]">
          This offer will be automatically accepted
        </p>
        <div className="border-b" />
        <div className="space-x-2">
          {discountTypes.map(({ id, name }) => (
            <Tag
              key={id}
              aria-selected={discountType === id}
              onClick={() => form.setValue('type', id)}
              className="cursor-pointer bg-secondary-50 hover:bg-teal-500/10 aria-selected:bg-teal-500/10 dark:bg-secondary-600 hover:dark:bg-teal-500/30 aria-selected:dark:bg-teal-500/30"
            >
              {name}
            </Tag>
          ))}
        </div>
        <FormProvider {...form}>
          <form
            id={PROMOTIONAL_OFFER_FOR_FAN_FORM_ID}
            className="space-y-6"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            {discountType === 'subscription' ? (
              <>
                <FormSelect
                  key="percent"
                  name="subscription.percent"
                  options={subscriptionPercentOptions}
                  label="Discount percent"
                />
                <FormSelect
                  key="subscription.duration"
                  name="subscription.duration"
                  options={monthsOptions}
                  label="Duration"
                />
              </>
            ) : (
              <>
                <FormSelect
                  key="freeTrial.duration"
                  name="freeTrial.duration"
                  options={daysOptions}
                  label="Duration"
                />
                <FormSelect
                  key="offerExpiration"
                  name="freeTrial.offerExpiration"
                  options={daysOptions}
                  label="Offer expiration"
                />
              </>
            )}
          </form>
        </FormProvider>
        <Dialog.ClosableFooter
          onClose={onClose}
          className="grid-cols-3"
        >
          <Button
            variant="outline"
            onClick={() => form.reset(promotionalOfferFormDefaultValues)}
          >
            Clear
          </Button>
          <Button
            type="submit"
            form={PROMOTIONAL_OFFER_FOR_FAN_FORM_ID}
          >
            Apply
          </Button>
        </Dialog.ClosableFooter>
      </div>
    </Dialog>
  )
}
